<template>
  <div v-if="currentDelivery !== undefined" id="content-page-table" class="table-fix-head" :style="fixHeadCalculatedStyle">
    <table class="table table-borderless table-sm table-fit align-middle">
      <thead>
        <tr>
          <th>
            {{ $t('field.generic.item') }}
            <font-awesome-icon :icon="['fas', 'sort-up']" />
          </th>
          <th>{{ $t('field.generic.quantity') }}</th>
          <th class="d-none d-sm-table-cell">{{ $t('field.masterdata.lot') }}</th>
          <th class="d-none d-sm-table-cell" v-if="hasBestBeforeOnAnyLine">{{ $t('field.masterdata.bestBefore') }}</th>
          <th class="d-none d-sm-table-cell" v-if="hasExpiryOnAnyLine">{{ $t('field.masterdata.expiry') }}</th>
          <th v-if="userSettingsStore.data.showBarcode"></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(entry, index) in sortedList" :key="index">
          <tr class="item-row-one">
            <td><ItemField :item="entry.item" /></td>
            <td><QuantityField :quantity="entry.quantity" :unit-name="entry.item.baseUnitCode" :pad-length="maxQuantityLength" /></td>
            <td class="d-none d-sm-table-cell"><LotField :item="entry.item" :lot="entry.lot" /></td>
            <td class="d-none d-sm-table-cell text-center" v-if="hasBestBeforeOnAnyLine"><template v-if="entry.bestBefore">{{ $d(new Date(entry.bestBefore), 'dateonly') }}</template></td>
            <td class="d-none d-sm-table-cell text-center" v-if="hasExpiryOnAnyLine"><template v-if="entry.expiry">{{ $d(new Date(entry.expiry), 'dateonly') }}</template></td>
            <td v-if="userSettingsStore.data.showBarcode" class="bg-white p-1">
              <TradeItemBarcode :itemId="entry.item.id" :itemSignature="entry" :size="'3rem'" />
            </td>
          </tr>
          <tr class="d-table-row d-sm-none" v-if="entry.lot || entry.bestBefore || entry.expiry">
            <td :colspan="4 + (hasBestBeforeOnAnyLine ? 0 : 1) + (hasExpiryOnAnyLine ? 1 : 0) + (userSettingsStore.data.showBarcode ? 1 : 0)">
              <div class="d-flex gap-2 justify-content-between ps-3">
                <LotField :item="entry.item" :lot="entry.lot" />
                <template v-if="entry.bestBefore">{{ $d(new Date(entry.bestBefore), 'dateonly') }}</template>
                <template v-if="entry.expiry">{{ $d(new Date(entry.expiry), 'dateonly') }}</template>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
  <div v-else>
    <slot name="empty-list" />
  </div>

</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useDeliveriesStore, useUserSettingsStore } from '@/stores';
import { ItemField, QuantityField, LotField } from '@/components/fields';
import TradeItemBarcode from "@/components/diagram/TradeItemBarcode.vue";
import { compareOptionalString } from '@/services';

const userSettingsStore = useUserSettingsStore();
const deliveriesStore = useDeliveriesStore();

const props = defineProps({
  deliveryId: { type: Number, required: true }
});

const offsetTop = ref(0);

onMounted(() => {
  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const sortedList = computed(() => {
  if (currentDelivery.value === undefined) {
    return [];
  }
  return [...currentDelivery.value.lines].sort((a, b) =>
    a.item.name.localeCompare(b.item.name) ||
    compareOptionalString(a.expiry, b.expiry) ||
    compareOptionalString(a.bestBefore, b.bestBefore) ||
    compareOptionalString(a.lot, b.lot)
  );
});

const maxQuantityLength = computed(() => {
  return sortedList.value.reduce((acc, item) => Math.max(acc, item.quantity.toString().length), 0);
});

const currentDelivery = computed(() => {
  return deliveriesStore.getById(props.deliveryId)
});

const hasBestBeforeOnAnyLine = computed(() => {
  return currentDelivery.value !== undefined && currentDelivery.value.lines.findIndex((line) => !!line.bestBefore) !== -1;
});

const hasExpiryOnAnyLine = computed(() => {
  return currentDelivery.value !== undefined && currentDelivery.value.lines.findIndex((line) => !!line.expiry) !== -1;
});
</script>

<style scoped type="scss">
tr.item-row-one {
  border-top: 1px dashed #ccc;

  &:first-child {
    border-top: none;
  }
}
</style>
